import React from 'react';
import { X, FileText, CheckCircle } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const ConsentModal = ({ open, onClose, onAccept }) => {
  const [consent, setConsent] = React.useState(false);

  const handleAccept = async () => {
    if (consent) {
      // Spara policy-acceptans på users/{uid} för spårbarhet (GDPR)
      const uid = auth.currentUser?.uid;
      if (uid) {
        try {
          await updateDoc(doc(db, 'users', uid), {
            policyAcceptedAt: new Date().toISOString(),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Kunde inte spara policyAcceptedAt:', err);
        }
      }
      onAccept();
      handleClose();
    }
  };

  const handleClose = () => {
    setConsent(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 p-4"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto overflow-hidden transform transition-all duration-300 ease-out"
           style={{ maxHeight: '90vh', overflowY: 'auto' }}>

        {/* Header */}
        <div className="px-6 py-8 text-center relative" style={{ backgroundColor: '#62B8E9' }}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="text-white" size={32} />
          </div>
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Villkor för ansökan
          </h2>
          <p className="text-white text-sm opacity-90">
            Läs och godkänn villkoren innan du fortsätter
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-gray-50 rounded-xl p-5 mb-6">
            <h3 className="font-semibold text-[#222222] mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Viktig information om andrahandsuthyrning
            </h3>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: '#62B8E9' }}>
                  <span className="block w-full h-full flex items-center justify-center text-white text-xs font-bold">1</span>
                </div>
                <p className="text-sm text-[#626262] leading-relaxed">
                  <span className="font-semibold text-[#222222]">Avgift:</span> Hyria äger rätt att debitera bostadsrättsägaren en avgift om 3,95 % av det totala hyresbeloppet per månad.
                </p>
              </div>

              <div className="flex gap-3">
                <div className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: '#62B8E9' }}>
                  <span className="block w-full h-full flex items-center justify-center text-white text-xs font-bold">2</span>
                </div>
                <p className="text-sm text-[#626262] leading-relaxed">
                  <span className="font-semibold text-[#222222]">Kreditprövning:</span> En personupplysning samt kreditprövning kommer att genomföras avseende den angivna hyresgästen.
                </p>
              </div>

              <div className="flex gap-3">
                <div className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: '#62B8E9' }}>
                  <span className="block w-full h-full flex items-center justify-center text-white text-xs font-bold">3</span>
                </div>
                <p className="text-sm text-[#626262] leading-relaxed">
                  <span className="font-semibold text-[#222222]">Hemförsäkring:</span> Innan ett hyresavtal kan upprättas ska hyresgästen teckna en giltig hemförsäkring som täcker hela den avtalade hyresperioden. Försäkringsbrevet ska tillhandahållas Hyria för inkludering i hyresavtalet.
                </p>
              </div>

              <div className="flex gap-3">
                <div className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: '#62B8E9' }}>
                  <span className="block w-full h-full flex items-center justify-center text-white text-xs font-bold">4</span>
                </div>
                <p className="text-sm text-[#626262] leading-relaxed">
                  <span className="font-semibold text-[#222222]">Hyresavtal:</span> Hyria ansvarar för att upprätta hyresavtalet, och den sökande förbinder sig att upprätta hyresavtalet via Hyria.
                </p>
              </div>
            </div>
          </div>

          {/* Consent checkbox */}
          <label className="flex items-start gap-3 mb-6 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#62B8E9] focus:ring-[#62B8E9] focus:ring-2 cursor-pointer"
              style={{ accentColor: '#62B8E9' }}
            />
            <span className="text-sm font-semibold text-[#222222] select-none">
              Jag ger mitt samtycke till ovanstående villkor
            </span>
          </label>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 text-[#626262] font-semibold py-3 px-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors focus:outline-none"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Jag ger inte mitt samtycke
            </button>
            <button
              onClick={handleAccept}
              disabled={!consent}
              className={`flex-1 font-semibold py-3 px-4 rounded-xl transition-all focus:outline-none flex items-center justify-center gap-2 ${
                consent
                  ? 'bg-[#62B8E9] text-white hover:opacity-90'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {consent && <CheckCircle size={18} />}
              Fortsätt till ansökan
            </button>
          </div>

          {!consent && (
            <p className="text-center text-xs text-gray-500 mt-4 italic">
              Du måste godkänna villkoren för att kunna fortsätta med ansökan
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentModal;